/**
 * Shared price utility functions — used by CardTile, CardDetailModal,
 * CollectionView, PackOpening, and any component that reads price settings
 * or formats price values.
 */

import { getDisplayPriceValue } from './filterOptions.js';

/**
 * Read price settings from localStorage.
 * Returns { showPrices: boolean, pricePeriod: string }.
 */
export function readPriceSettings() {
  if (typeof window === 'undefined') {
    return { showPrices: false, pricePeriod: '7' };
  }
  const show = localStorage.getItem('riftbound_show_prices');
  const period = localStorage.getItem('riftbound_price_period') || '7';
  return {
    showPrices: show !== 'false',
    pricePeriod: period
  };
}

/**
 * Format a EUR price value for display (e.g. 1.5 → "€1.50").
 * Returns null when the value is missing or non-finite.
 */
export function formatPriceValue(eur) {
  if (eur == null) return null;
  const n = Number(eur);
  if (!Number.isFinite(n)) return null;
  return `€${n.toFixed(2)}`;
}

/** Rarities that are foil-only in non-OGS sets. */
export const FOIL_ONLY_RARITIES = new Set(['Rare', 'Epic', 'Showcase']);

/**
 * Human-readable period label (e.g. '7' → '7d').
 */
export function periodLabel(period) {
  if (period === '1' || period === 1) return '1d';
  if (period === '30' || period === 30) return '30d';
  return '7d';
}

/**
 * Compute raw normal and foil prices for a card, taking set rules into account.
 *
 * @param {object} card  Card object (must have card.price, card.set, card.id, etc.)
 * @param {string} rarity  Resolved rarity label (e.g. 'Rare', 'Common')
 * @returns {{ rawNormal: number|null, rawFoil: number|null, isFoilOnly: boolean, isOgsSet: boolean }}
 */
export function computeRawPrices(card, rarity) {
  const setCode = card.set?.value?.id || (card.id ? String(card.id).split('-')[0] : '');
  const isOgsSet = setCode === 'OGS';
  const isFoilOnly = !isOgsSet && FOIL_ONLY_RARITIES.has(rarity);

  const rawNormal =
    !isFoilOnly && card.price?.normal != null && Number.isFinite(Number(card.price.normal))
      ? Number(card.price.normal)
      : null;
  const rawFoil =
    !isOgsSet && card.price?.foil != null && Number.isFinite(Number(card.price.foil))
      ? Number(card.price.foil)
      : null;

  return { rawNormal, rawFoil, isFoilOnly, isOgsSet };
}

/**
 * Compute the display price for a card (the single summary value used in tiles).
 *
 * @param {object} card       Full card object with card.price
 * @param {string} rarity     Resolved rarity label
 * @param {string} cardType   Resolved card type label
 * @param {boolean} isPromo   Whether the card is promo
 * @returns {number|null}     The display-worthy price, or null
 */
export function computeDisplayPrice(card, rarity, cardType, isPromo) {
  if (!card.price) return null;
  const { rawNormal, rawFoil } = computeRawPrices(card, rarity);
  return getDisplayPriceValue(
    { ...card.price, normal: rawNormal, foil: rawFoil },
    rarity,
    cardType,
    isPromo
  );
}
